import { FCAData } from "@/mocks/fca";
import { estadoFitoConfig, riscoConfig } from "@/mocks/cta";

interface Props {
  data: FCAData;
}

type ConditionLevel = "bom" | "regular" | "ruim" | "critico" | "ausente" | "nao-observado";

const conditionBadge: Record<ConditionLevel, string> = {
  bom: "bg-primary-100 text-primary-700",
  regular: "bg-caution-100 text-caution-700",
  ruim: "bg-warning-100 text-warning-700",
  critico: "bg-danger-100 text-danger-700",
  ausente: "bg-background-100 text-foreground-500",
  "nao-observado": "bg-background-100 text-foreground-500",
};

const conditionLabel: Record<ConditionLevel, string> = {
  bom: "Bom",
  regular: "Regular",
  ruim: "Ruim",
  critico: "Crítico",
  ausente: "Ausente",
  "nao-observado": "Não observado",
};

export default function TreeHealthRiskPanel({ data }: Props) {
  const fito = data.fitossanidade;
  const estadoFito = estadoFitoConfig[data.estadoFitossanitario] || { badge: "", label: data.estadoFitossanitario };
  const risco = riscoConfig[data.classeRisco] || { badge: "", label: data.classeRisco };

  const getConditionLevel = (value: string): ConditionLevel => {
    const v = value.toLowerCase();
    if (v.includes("bem") || v.includes("boa") || v.includes("excelente") || v.includes("adequado")) return "bom";
    if (v.includes("regular") || v.includes("median")) return "regular";
    if (v.includes("ruim") || v.includes("compromet")) return "ruim";
    if (v.includes("crítico") || v.includes("grave")) return "critico";
    if (v.includes("ausência") || v.includes("ausente") || v.includes("não observad")) return "ausente";
    return "nao-observado";
  };

  return (
    <div className="space-y-5">
      {/* Estado Geral + Risco */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-card border border-background-200/70 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
            <i className="ri-heart-pulse-line text-primary-500"></i>
            Estado Fitossanitário Geral
          </h3>
          <div className="flex items-center gap-3">
            <span className={`badge text-sm px-3 py-1.5 ${estadoFito.badge}`}>
              <i className="ri-check-line mr-1"></i>
              {estadoFito.label}
            </span>
          </div>
          <p className="text-sm text-foreground-600 mt-3">{fito.estadoGeral}</p>
        </div>

        <div className="bg-white rounded-card border border-background-200/70 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
            <i className="ri-alert-line text-warning-500"></i>
            Classe de Risco
          </h3>
          <div className="flex items-center gap-3">
            <span className={`badge text-sm px-3 py-1.5 ${risco.badge}`}>
              <i className="ri-shield-line mr-1"></i>
              {risco.label}
            </span>
          </div>
          {fito.criteriosRisco.length > 0 && (
            <div className="mt-3 space-y-1">
              <p className="text-[11px] text-foreground-500 uppercase tracking-wide mb-1">Critérios observados</p>
              {fito.criteriosRisco.map((c, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs text-foreground-700">
                  <i className="ri-checkbox-circle-line text-primary-500 text-xs"></i>
                  {c}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detailed conditions */}
      <div className="bg-white rounded-card border border-background-200/70 p-5">
        <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-4 flex items-center gap-2">
          <i className="ri-search-line text-accent-500"></i>
          Avaliação Detalhada
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <ConditionItem label="Condição da Copa" value={fito.condicaoCopa} />
          <ConditionItem label="Condição do Tronco" value={fito.condicaoTronco} />
          <ConditionItem label="Condição das Raízes" value={fito.condicaoRaizes} />
          <ConditionItem label="Pragas" value={fito.pragas || "Ausência de pragas"} />
          <ConditionItem label="Doenças" value={fito.doencas || "Ausência de doenças"} />
          <ConditionItem label="Fungos" value={fito.fungos} />
          <ConditionItem label="Cupins" value={fito.cupins} />
          <ConditionItem label="Cavidades" value={fito.cavidades} />
          <ConditionItem label="Podridão" value={fito.podridao} />
          <ConditionItem label="Galhos Secos" value={fito.galhosSecos} />
          <ConditionItem label="Inclinação" value={fito.inclinacao} />
          <ConditionItem label="Raízes Expostas" value={fito.raizesExpostas} />
        </div>
      </div>

      {/* Technical opinion */}
      <div className="bg-white rounded-card border border-background-200/70 p-5">
        <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
          <i className="ri-file-text-line text-foreground-600"></i>
          Parecer Técnico
        </h3>
        <p className="text-sm text-foreground-700 leading-relaxed bg-background-50 p-4 rounded-lg border border-background-200/70">
          {fito.parecerTecnico}
        </p>
      </div>
    </div>
  );
}

function ConditionItem({ label, value }: { label: string; value: string }) {
  const level = (() => {
    const v = value.toLowerCase();
    if (v.includes("bem") || v.includes("boa") || v.includes("excelente") || v.includes("adequado") || v.includes("íntegra")) return "bom";
    if (v.includes("regular") || v.includes("median") || v.includes("ocasionais")) return "regular";
    if (v.includes("ruim") || v.includes("compromet")) return "ruim";
    if (v.includes("crítico") || v.includes("grave")) return "critico";
    if (v.includes("ausência") || v.includes("ausente") || v.includes("não observad")) return "ausente";
    return "nao-observado";
  })() as ConditionLevel;

  return (
    <div className="p-3 bg-background-50 rounded-lg border border-background-200/70">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[11px] text-foreground-500 uppercase tracking-wide">{label}</p>
        <span className={`badge text-[10px] ${conditionBadge[level]}`}>{conditionLabel[level]}</span>
      </div>
      <p className="text-xs text-foreground-700 leading-relaxed">{value}</p>
    </div>
  );
}